import _React, { useEffect, useMemo, useRef, useState } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'
import {
  REMITANO_AUDIT_DEFAULT_LIMIT,
  REMITANO_AUDIT_CONCEPTION_YMD,
} from '../../lib/remitanoDepositAuditLimits.js'

const CSV_COLUMNS = [
  'aureus_date',
  'remitano_date',
  'hash',
  'network',
  'wallet_sender',
  'wallet_blockchain_sender',
  'wallet_receiver',
  'remitano_wallet',
  'remitano_amount',
  'aureus_amount',
  'username',
  'full_name',
  'user_id',
  'cellphone',
  'email',
  'country',
  'amount_bought',
  'shares_purchased',
  'bonus_shares',
  'phase',
  'match_class',
  'mismatch_reason',
  'aureus_status',
  'remitano_status',
] as const

function csvEscape(value: unknown) {
  const s = value == null ? '' : String(value)
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function downloadAuditCsv(rows: Array<Record<string, unknown>>, start: string, end: string) {
  const header = CSV_COLUMNS.join(',')
  const lines = rows.map((row) => CSV_COLUMNS.map((key) => csvEscape(row[key])).join(','))
  const blob = new Blob(['\ufeff' + header + '\n' + lines.join('\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `remitano-aureus-audit-${start || 'hash'}-${end || 'hash'}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

interface AuditRow {
  aureus_date?: string
  remitano_date?: string
  hash?: string
  remitano_wallet?: string
  wallet_sender?: string
  remitano_amount?: number | string
  aureus_amount?: number | string
  username?: string
  cellphone?: string
  email?: string
  amount_bought?: number | string
  shares_purchased?: number
  bonus_shares?: number
  phase?: string
  match_class?: string
  mismatch_reason?: string
  highlighted?: boolean
  aureus_status?: string
  remitano_status?: string
  country?: string
}

interface RemitanoDepositAuditPanelProps {
  onBack: () => void
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function pad2(n: number) {
  return String(n).padStart(2, '0')
}

function toYmd(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function parseYmd(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const [y, m, day] = value.split('-').map(Number)
  const d = new Date(y, m - 1, day)
  if (d.getFullYear() !== y || d.getMonth() !== m - 1 || d.getDate() !== day) return null
  return d
}

function todayYmd() {
  return toYmd(new Date())
}

function addDaysYmd(value: string, days: number) {
  const d = parseYmd(value)
  if (!d) return value
  d.setDate(d.getDate() + days)
  return toYmd(d)
}

function monthStartYmd(value: string) {
  const d = parseYmd(value)
  if (!d) return value
  return toYmd(new Date(d.getFullYear(), d.getMonth(), 1))
}

function formatDisplay(value: string) {
  const d = parseYmd(value)
  if (!d) return 'Select date'
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function toStartIso(dateValue: string) {
  const d = parseYmd(dateValue)
  if (!d) return null
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

function toEndIso(dateValue: string) {
  const d = parseYmd(dateValue)
  if (!d) return null
  d.setHours(23, 59, 59, 999)
  return d.toISOString()
}

function monthCells(year: number, month: number) {
  const first = new Date(year, month, 1)
  const mondayOffset = (first.getDay() + 6) % 7
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells: Array<number | null> = []
  for (let i = 0; i < mondayOffset; i += 1) cells.push(null)
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day)
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

function DateField({
  label,
  value,
  onChange,
  min,
  max,
  open,
  onToggle,
  onClose,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  min?: string
  max?: string
  open: boolean
  onToggle: () => void
  onClose: () => void
}) {
  const selected = parseYmd(value) || new Date()
  const [viewYear, setViewYear] = useState(selected.getFullYear())
  const [viewMonth, setViewMonth] = useState(selected.getMonth())

  useEffect(() => {
    if (!open) return
    const d = parseYmd(value)
    if (!d) return
    setViewYear(d.getFullYear())
    setViewMonth(d.getMonth())
  }, [open, value])

  function shiftMonth(delta: number) {
    const next = new Date(viewYear, viewMonth + delta, 1)
    setViewYear(next.getFullYear())
    setViewMonth(next.getMonth())
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#9ca3af', position: 'relative' }}>
      <span>{label}</span>
      <button
        type="button"
        onClick={onToggle}
        style={{
          padding: '10px 12px',
          borderRadius: 6,
          border: open ? '1px solid #d4af37' : '1px solid #333',
          background: '#0a0a0a',
          color: '#fff',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 14,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: 42,
        }}
      >
        <span>{formatDisplay(value)}</span>
        <span style={{ color: '#d4af37', fontSize: 13 }}>▾</span>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 40,
            marginTop: 6,
            width: 292,
            background: '#111827',
            border: '1px solid #374151',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
            padding: 12,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <button type="button" onClick={() => shiftMonth(-1)} style={navBtn}>
              ‹
            </button>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>
              {MONTHS[viewMonth]} {viewYear}
            </div>
            <button type="button" onClick={() => shiftMonth(1)} style={navBtn}>
              ›
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 6 }}>
            {WEEKDAYS.map((d) => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', padding: '2px 0' }}>
                {d}
              </div>
            ))}
            {monthCells(viewYear, viewMonth).map((day, i) => {
              if (!day) return <div key={`e-${i}`} />
              const ymd = `${viewYear}-${pad2(viewMonth + 1)}-${pad2(day)}`
              const isSelected = ymd === value
              const isToday = ymd === todayYmd()
              const disabled = Boolean((min && ymd < min) || (max && ymd > max))
              return (
                <button
                  key={ymd}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    onChange(ymd)
                    onClose()
                  }}
                  style={{
                    height: 32,
                    borderRadius: 6,
                    border: isToday && !isSelected ? '1px solid #d4af37' : '1px solid transparent',
                    background: isSelected ? '#d4af37' : 'transparent',
                    color: disabled ? '#4b5563' : isSelected ? '#111' : '#fff',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontSize: 12,
                    fontWeight: isSelected ? 700 : 500,
                  }}
                >
                  {day}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

const navBtn: _React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 6,
  border: '1px solid #374151',
  background: '#1f2937',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 16,
}

export function RemitanoDepositAuditPanel({ onBack }: RemitanoDepositAuditPanelProps) {
  const today = todayYmd()
  const [hash, setHash] = useState('')
  const [startDate, setStartDate] = useState(() => addDaysYmd(today, -13))
  const [endDate, setEndDate] = useState(today)
  const [openPicker, setOpenPicker] = useState<'start' | 'end' | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<AuditRow[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [meta, setMeta] = useState<string>('')
  const pickerWrapRef = useRef<HTMLDivElement>(null)

  const mismatchCount = useMemo(
    () => rows.filter((r) => r.match_class && r.match_class !== 'MATCHED').length,
    [rows]
  )

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!pickerWrapRef.current?.contains(e.target as Node)) setOpenPicker(null)
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenPicker(null)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [])

  function applyStart(next: string) {
    setStartDate(next)
    if (endDate < next) setEndDate(next)
  }

  function applyEnd(next: string) {
    setEndDate(next)
    if (startDate > next) setStartDate(next)
  }

  function applyPreset(kind: 'today' | '7' | '14' | 'month' | 'conception') {
    const end = todayYmd()
    if (kind === 'today') {
      setStartDate(end)
      setEndDate(end)
    } else if (kind === '7') {
      setStartDate(addDaysYmd(end, -6))
      setEndDate(end)
    } else if (kind === '14') {
      setStartDate(addDaysYmd(end, -13))
      setEndDate(end)
    } else if (kind === 'conception') {
      setStartDate(REMITANO_AUDIT_CONCEPTION_YMD)
      setEndDate(end)
    } else {
      setStartDate(monthStartYmd(end))
      setEndDate(end)
    }
    setOpenPicker(null)
  }

  async function run(operation: 'search_hash' | 'search_range') {
    setLoading(true)
    setError(null)
    try {
      const headers = await getSessionHeaders()
      let body: Record<string, unknown>
      if (operation === 'search_hash') {
        body = { operation, data: { hash: hash.trim() } }
      } else {
        const startIso = toStartIso(startDate)
        const endIso = toEndIso(endDate)
        if (!startIso || !endIso) {
          throw new Error('Choose a start date and an end date')
        }
        if (Date.parse(endIso) < Date.parse(startIso)) {
          throw new Error('End date must be on or after the start date')
        }
        body = {
          operation,
          data: {
            startDate: startIso,
            endDate: endIso,
            limit: REMITANO_AUDIT_DEFAULT_LIMIT,
          },
        }
      }

      const res = await fetch('/api/admin/remitano-deposit-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setRows(json.rows || [])
      setCounts(json.counts || {})
      setMeta(
        [
          json.cpt_capped ? `Aureus page hit ${json.cpt_count} rows — raise REMITANO_AUDIT_FULL_MAX` : null,
          json.remitano_enrich_capped ? `Remitano live match applied to the newest ${json.limit || REMITANO_AUDIT_DEFAULT_LIMIT} hashes; older Aureus rows still listed` : null,
          json.remitano_configured === false ? 'Remitano API not configured — Aureus CPT rows only' : null,
          json.remitano_list_scanned ? `Remitano list scanned (${json.remitano_pages_scanned || 0} pages)` : null,
        ]
          .filter(Boolean)
          .join(' · ')
      )
    } catch (e: any) {
      setError(e?.message || 'Audit failed')
      setRows([])
      setCounts({})
      setMeta('')
    } finally {
      setLoading(false)
    }
  }

  function rowBg(r: AuditRow) {
    if (!r.match_class || r.match_class === 'MATCHED') return 'transparent'
    if (r.match_class === 'AMOUNT_MISMATCH') return 'rgba(220, 38, 38, 0.18)'
    if (r.match_class === 'REMITANO_ONLY') return 'rgba(234, 179, 8, 0.16)'
    if (r.match_class === 'REMITANO_NOT_FOUND') return 'rgba(59, 130, 246, 0.16)'
    return 'rgba(249, 115, 22, 0.16)'
  }

  const presetBtn = (active: boolean): _React.CSSProperties => ({
    padding: '6px 10px',
    borderRadius: 6,
    border: active ? '1px solid #d4af37' : '1px solid #333',
    background: active ? 'rgba(212,175,55,0.15)' : '#111',
    color: active ? '#fbbf24' : '#d1d5db',
    cursor: 'pointer',
    fontSize: 12,
  })

  const isToday = startDate === today && endDate === today
  const is7 = startDate === addDaysYmd(today, -6) && endDate === today
  const is14 = startDate === addDaysYmd(today, -13) && endDate === today
  const isMonth = startDate === monthStartYmd(today) && endDate === today
  const isConception = startDate === REMITANO_AUDIT_CONCEPTION_YMD && endDate === today

  return (
    <div style={{ color: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22 }}>Database audit — Remitano ↔ Aureus</h2>
          <p style={{ margin: '6px 0 0', color: '#9ca3af', fontSize: 13 }}>
            Search by hash and/or date. Mismatches and Remitano-only rows stay in the result set and are highlighted.
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', cursor: 'pointer' }}
        >
          Change tool
        </button>
      </div>

      <div ref={pickerWrapRef}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr 1fr auto auto',
            gap: 10,
            marginBottom: 10,
            alignItems: 'end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#9ca3af' }}>
            Transaction hash
            <input
              value={hash}
              onChange={(e) => setHash(e.target.value)}
              placeholder="0x… or TRON hash"
              style={{ padding: 10, borderRadius: 6, border: '1px solid #333', background: '#0a0a0a', color: '#fff' }}
            />
          </label>
          <DateField
            label="Start date"
            value={startDate}
            onChange={applyStart}
            max={today}
            open={openPicker === 'start'}
            onToggle={() => setOpenPicker(openPicker === 'start' ? null : 'start')}
            onClose={() => setOpenPicker(null)}
          />
          <DateField
            label="End date"
            value={endDate}
            onChange={applyEnd}
            min={startDate}
            max={today}
            open={openPicker === 'end'}
            onToggle={() => setOpenPicker(openPicker === 'end' ? null : 'end')}
            onClose={() => setOpenPicker(null)}
          />
          <button
            type="button"
            disabled={loading || hash.trim().length < 8}
            onClick={() => run('search_hash')}
            style={{ padding: '10px 14px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', opacity: loading || hash.trim().length < 8 ? 0.7 : 1 }}
          >
            Search hash
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => run('search_range')}
            style={{ padding: '10px 14px', borderRadius: 6, border: 'none', background: '#059669', color: '#fff', cursor: 'pointer', opacity: loading ? 0.7 : 1 }}
          >
            Search range
          </button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Quick range</span>
          <button type="button" onClick={() => applyPreset('today')} style={presetBtn(isToday)}>
            Today
          </button>
          <button type="button" onClick={() => applyPreset('7')} style={presetBtn(is7)}>
            Last 7 days
          </button>
          <button type="button" onClick={() => applyPreset('14')} style={presetBtn(is14)}>
            Last 14 days
          </button>
          <button type="button" onClick={() => applyPreset('month')} style={presetBtn(isMonth)}>
            This month
          </button>
          <button type="button" onClick={() => applyPreset('conception')} style={presetBtn(isConception)}>
            From conception
          </button>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            {formatDisplay(startDate)} → {formatDisplay(endDate)}
          </span>
        </div>
      </div>

      {error && <div style={{ color: '#fca5a5', marginBottom: 10 }}>{error}</div>}
      {loading && <div style={{ color: '#9ca3af', marginBottom: 10 }}>Running read-only Remitano + Aureus reconcile…</div>}

      {!!rows.length && (
        <div style={{ marginBottom: 10, fontSize: 13, color: '#d1d5db' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>
              Rows: {rows.length} · Exceptions: {mismatchCount}
              {Object.entries(counts).map(([k, v]) => (
                <span key={k} style={{ marginLeft: 10 }}>
                  {k}: {v}
                </span>
              ))}
            </span>
            <button
              type="button"
              onClick={() => downloadAuditCsv(rows as Array<Record<string, unknown>>, startDate, endDate)}
              style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d4af37', background: '#111', color: '#fbbf24', cursor: 'pointer', fontSize: 12 }}
            >
              Export CSV
            </button>
          </div>
          {meta ? <div style={{ color: '#9ca3af', marginTop: 4 }}>{meta}</div> : null}
        </div>
      )}

      <div style={{ overflow: 'auto', maxHeight: '70vh', border: '1px solid #222', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#111', textAlign: 'left' }}>
              {[
                'Date',
                'Hash',
                'Wallet',
                'Remitano $',
                'Aureus $',
                'Username',
                'Cell',
                'Email',
                'Bought',
                'Shares',
                'Phase',
                'Match',
              ].map((h) => (
                <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid #222', whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.hash}-${i}`} style={{ background: rowBg(r) }}>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>{r.remitano_date || r.aureus_date}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a', fontFamily: 'monospace', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.hash}>
                  {r.hash}
                </td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a', fontFamily: 'monospace', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.remitano_wallet || r.wallet_sender}
                </td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>{r.remitano_amount}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>{r.aureus_amount}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>{r.username}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>{r.cellphone}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>{r.email}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>{r.amount_bought}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>
                  {r.shares_purchased}
                  {r.bonus_shares ? ` +${r.bonus_shares}` : ''}
                </td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>{r.phase}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }} title={r.mismatch_reason || ''}>
                  {r.match_class}
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr>
                <td colSpan={12} style={{ padding: 20, color: '#9ca3af', textAlign: 'center' }}>
                  Run a hash or date-range search to load Remitano ↔ Aureus rows.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
